import type { SupabaseClient } from '@supabase/supabase-js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface TicketRow {
  id: string;
  tenant_id: string;
  ticket_no: string;
  subject: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  contact_name: string | null;
  contact_email: string | null;
  assignee_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  sla_response_due: string | null;
  sla_resolve_due: string | null;
}

export interface UseTicketsOptions {
  search?: string;
  status?: TicketRow['status'];
  priority?: TicketRow['priority'];
  limit?: number;
}

export function useTickets(
  supabase: SupabaseClient,
  tenantId: string,
  opts: UseTicketsOptions = {},
) {
  return useQuery({
    queryKey: ['tickets', tenantId, opts],
    queryFn: async () => {
      let q = supabase
        .from('support_tickets')
        .select('*', { count: 'exact' })
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false })
        .limit(opts.limit ?? 50);
      if (opts.status) q = q.eq('status', opts.status);
      if (opts.priority) q = q.eq('priority', opts.priority);
      if (opts.search) q = q.ilike('subject', `%${opts.search}%`);
      const { data, count, error } = await q;
      if (error) throw error;
      return { data: data as TicketRow[], total: count ?? 0 };
    },
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

export function useCreateTicket(supabase: SupabaseClient) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      tenant_id: string;
      subject: string;
      priority?: TicketRow['priority'];
      contact_name?: string;
      contact_email?: string;
    }) => {
      const { data, error } = await supabase
        .from('support_tickets')
        .insert(payload)
        .select()
        .single();
      if (error) throw error;
      return data as TicketRow;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['tickets', vars.tenant_id] });
    },
  });
}

export function useUpdateTicketStatus(supabase: SupabaseClient) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      status,
      tenantId: _tenantId,
      resolution_note,
    }: {
      id: string;
      status: TicketRow['status'];
      tenantId: string;
      resolution_note?: string | null;
    }) => {
      void _tenantId;
      void resolution_note;
      const extra: Record<string, string> = {};
      if (status === 'resolved') extra.resolved_at = new Date().toISOString();
      const { error } = await supabase
        .from('support_tickets')
        .update({ status, ...extra })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['tickets', vars.tenantId] });
    },
  });
}
